import {Alert} from 'src/design/atoms/Alert';
import {List} from 'src/design/atoms/List';
import {useDisclosure} from '@mantine/hooks';
import React, {useEffect, useState} from 'react';
import type {NotificationRuleResponse} from 'src/api/ProactApiClient';
import {EntityIdentifierEnum, ModuleEnum} from 'src/api/ProactApiClient';
import {
  useCreateNotificationRuleMutation,
  useDeleteNotificationRuleMutation,
  useGetNotificationRuleQuery,
  useGetNotificationRuleVariablesQuery,
  useUpdateNotificationRuleMutation,
} from 'src/redux/queries/notificationRule';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {SidebarLinkNotificationRules} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {NotificationsModule} from 'src/app/notifications/NotificationsModule';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useNotificationRulesBreadcrumbs} from 'src/shared/hooks/useNotificationRulesBreadcrumbs';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  CreateDefaultNotificationRule,
  GetNotificationRulePillConfig,
  MapNotificationRuleResponseToNotificationRuleRequest,
} from '../notificationRuleHelpers';
import {useNotificationRuleValidation} from '../validation/useNotificationRuleValidation';
import {EditNotificationRuleForm} from './EditNotificationRuleForm';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export type NotificationRuleViewType =
  | 'content'
  | 'notifications'
  | 'audit'
  | '';

export interface NotificationRuleFormProps {
  id?: number;
  mode: 'create' | 'edit' | 'view';
}
export const NotificationRuleForm: React.FC<NotificationRuleFormProps> = ({
  id,
  mode,
}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const [view, setView] = useState<NotificationRuleViewType>('content');

  const notifRulesPerms = useModulePermissions(ModuleEnum.NotificationRules);
  const auditPerms = useAuditLogPermissions();

  const [isSavePending, setIsSavePending] = useState(false);
  const [item, setItem] = useState<NotificationRuleResponse>(
    CreateDefaultNotificationRule()
  );

  const {data: fetchedItem, isLoading} = useGetNotificationRuleQuery(id!, {
    skip: id === undefined,
  });

  useEffect(() => {
    if (fetchedItem) {
      setItem(fetchedItem);
    }
  }, [fetchedItem]);

  const [createNotificationRule] = useCreateNotificationRuleMutation();
  const [updateNotificationRule] = useUpdateNotificationRuleMutation();
  const [deleteNotificationRule] = useDeleteNotificationRuleMutation();

  // Fetch available tokens for validation
  const {data: variablesResp} = useGetNotificationRuleVariablesQuery(
    {
      ruleType: item.ruleType,
      module: item.metadata.module ?? ModuleEnum.Invalid,
      courseChangeType: item.metadata.courseChangeType,
      formDefinitionId: item.metadata.formDefinitionId,
    },
    {}
  );
  const availableTokens = variablesResp?.variables ?? [];

  // Validation hook
  const {
    errors: validationErrors,
    validateForm,
    clearFieldError,
  } = useNotificationRuleValidation(availableTokens);

  const handleSave = async () => {
    try {
      if (item === undefined) {
        return;
      }

      // Validate before saving
      if (!validateForm(item)) {
        setAlert('Please fix the validation errors before saving', 'error');
        return;
      }

      setIsSavePending(true);

      const body = MapNotificationRuleResponseToNotificationRuleRequest(item);

      const res =
        id === undefined
          ? await createNotificationRule(body).unwrap()
          : await updateNotificationRule(body).unwrap();

      setAlert('Notification Rule was successfully updated', 'success');

      navigate(PathUtility.NotificationRuleView(res.id), {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };
  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (id === undefined) {
        return;
      }

      setIsSavePending(true);

      await deleteNotificationRule(id).unwrap();

      setAlert('Notification Rule was successfully archived', 'success');

      navigate(PathUtility.NotificationRule());
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const {breadcrumbs, fallbackTo} = useNotificationRulesBreadcrumbs({
    mode,
    ruleId: id,
    ruleName: item?.name,
  });

  const pills = GetNotificationRulePillConfig(item);

  const handleDataChange = (data: NotificationRuleResponse): void => {
    setItem(data);
    // Note: Don't clear errors here - they will be cleared when validation passes on save
  };

  const {RenderedTabsComponent} = useUrlQueryTabV2<NotificationRuleViewType>({
    view: (view ?? '') as NotificationRuleViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: (
          <>
            {item?.active === false && (
              <Alert title="Inactive Notification Rule" color="yellow">
                This notification rule is inactive. It will not be used to send
                notifications or to perform other functions.
              </Alert>
            )}

            {mode !== 'view' ? (
              <>
                <EditNotificationRuleForm
                  data={item}
                  onDataChange={handleDataChange}
                  mode={'edit'}
                  validationErrors={validationErrors}
                  onFieldChange={clearFieldError}
                />

                {Object.keys(validationErrors).length > 0 && (
                  <Alert
                    color="red"
                    title="Please fix the following issues before saving"
                    mt="md"
                    mb="md">
                    <List size="sm">
                      {Object.entries(validationErrors).map(
                        ([field, error]) =>
                          error && <List.Item key={field}>{error}</List.Item>
                      )}
                    </List>
                  </Alert>
                )}

                <SaveButton loading={isSavePending} onClick={handleSave} />
              </>
            ) : (
              <>
                <EditNotificationRuleForm
                  data={item}
                  onDataChange={handleDataChange}
                  mode={'view'}
                />
              </>
            )}
          </>
        ),
      },
      {
        view: 'notifications',
        label: 'Notifications',
        disabled: mode !== 'view' || !notifRulesPerms.notifications,
        component: (
          <>
            <NotificationsModule notificationRuleId={item?.id} />
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: mode !== 'view' || !auditPerms.view,
        component: item?.id && (
          <AuditModule
            entityId={String(item.id)}
            entityType={EntityIdentifierEnum.NotificationRule}
          />
        ),
      },
    ],
    onSelectView: view => setView(view as NotificationRuleViewType),
  });

  return !isLoading ? (
    <>
      <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkNotificationRules.icon}>
          {item ? item.name : 'New Notification Rule'}
        </PageHeader.Title>
        {pills.length > 0 && (
          <PageHeader.Badges>
            {pills.map((pill, index) => (
              <PageHeader.Badge key={index} color={pill.color} variant="light">
                {pill.text}
              </PageHeader.Badge>
            ))}
          </PageHeader.Badges>
        )}
        {mode === 'edit' && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}
        <PageHeader.Actions>
          {mode === 'view' && notifRulesPerms.update && (
            <PageHeader.Action
              variant="edit"
              onClick={() =>
                navigate(PathUtility.NotificationRuleEdit(String(id)))
              }>
              Edit
            </PageHeader.Action>
          )}
          {mode === 'view' && notifRulesPerms.archive && (
            <PageHeader.Action
              variant="delete"
              onClick={() => confirmDeleteHandlers.open()}>
              Archive
            </PageHeader.Action>
          )}
          {mode !== 'view' && (
            <PageHeader.Action
              variant="save"
              loading={isSavePending}
              onClick={handleSave}>
              Save
            </PageHeader.Action>
          )}
        </PageHeader.Actions>
      </PageHeader>
      {RenderedTabsComponent}

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to archive this notification rule?"
        body="The notification rule will be archived and no longer be accessible. Any notifications related to this rule will no longer be accessible. Other items that target this rule, such as Workflows, are not archived."
      />
    </>
  ) : (
    <TableBodyRender isLoading={isLoading} />
  );
};

export default NotificationRuleForm;
